import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  ReactNode,
  useState,
  useRef,
  useEffect,
} from "react";
import type { RootStackParamList } from "../navigation/navigation.types";
import { Animated } from "react-native";
import { useAppState, actions as appStateActions } from "./AppStateContext";

export enum Context {
  TRACK = "track",
  ASSIGNMENT = "assignment",
  AITUTOR = "ai_tutor",
  ONBOARDING = "onboarding",
  GENERAL = "general",
}

const mapRouteToContext = (route: keyof RootStackParamList, params?: unknown) => {
  switch (route) {
    case "Lesson Activities":
    case "Lesson Assessment":
    case "Lesson Chat":
      return Context.TRACK;
    case "Resource":
      return Context.ASSIGNMENT;
    case "Custom Resource Student":
    case "Custom Course AITutor":
      return Context.AITUTOR;
    case "Onboarding":
      return Context.ONBOARDING;
    default:
      return Context.GENERAL;
  }
};

export interface SchoolState {
  contentType: string;
  contentId: string;
  classId: string;
  integrationId: string;
}

// part of AppStateContext, subject to refactoring later
export interface LearningState {
  state: string;
  topic: string;
  topicAncestors: string[];
  label: string;
  isUnitAssessment: boolean;
  subchapterName: string;
  subchapterDescription: string;
  objectives: string;
  learningObjectiveId: string;
}

export enum ConversationType {
  GENERAL_CONVERSATION = "GENERAL_CONVERSATION",
  GET_SUGGESTIONS = "GET_SUGGESTIONS",
  SCHOOL = "SCHOOL",
  ONBOARD = "ONBOARD",
  LEARNING = "LEARNING",
}

interface LLMMeta {
  token: string;
  timeZone: string;
  abort: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  displayMessage?: boolean;
  attachmentId?: string;
  executionInfo?: {
    agentName: string;
    status: string;
    message: string;
  };
}

export interface LLMMessage {
  meta: LLMMeta;
  state: {
    context: Context;
    params: {
      [key: string]: string;
    };
    general: ConversationType;
    learning: LearningState;
    school: SchoolState;
    view?: string;
  };
  content: {
    message: string;
    author: "HUMAN" | "LLM";
    type: null;
  };
  audio?: {
    type: "HUMAN" | "AI_ABORT";
    data: string | null;
  };
}

// Define the state type
interface ChatBarState {
  llmMessage: LLMMessage;
  isRecording: boolean;
  isAudioOn: boolean;
  isSubmitEnabled: boolean;
  activeContext: Context;
  activeObjectId: string | null;
  activeChat: {
    id: string;
    context: Context;
  } | null;
}

// Define the initial state
const initialState: ChatBarState = {
  llmMessage: {
    meta: {
      token: "",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      abort: true,
      isExpanded: false,
      isLoading: false,
      displayMessage: true,
    },
    state: {
      context: Context.GENERAL,
      params: {},
      general: ConversationType.GENERAL_CONVERSATION,
      learning: {} as LearningState,
      school: {} as SchoolState,
    },
    content: {
      message: "",
      author: "HUMAN",
      type: null,
    },
  },
  isRecording: false,
  isAudioOn: false,
  isSubmitEnabled: true,
  activeContext: Context.GENERAL,
  activeObjectId: null,
  activeChat: null,
};

// Define the action types
type ChatBarAction =
  | { type: "TOGGLE_RECORDING" }
  | { type: "SET_AUDIO"; payload: boolean }
  | { type: "SET_SUBMIT_ENABLED"; payload: boolean }
  | { type: "SET_CONVERSATION_TYPE"; payload: ConversationType }
  | { type: "SET_SCHOOL_MESSAGE"; payload: SchoolState }
  | { type: "SET_LEARNING_MESSAGE"; payload: LearningState }
  | { type: "SET_CONTEXT"; payload: { context: Context; params: { [key: string]: string } } }
  | { type: "SET_ACTIVE_CONTEXT_AND_OBJECT_ID"; payload: { context: Context; objectId: string } }
  | { type: "SET_ACTIVE_CHAT"; payload: { id: string; context: Context } }
  | { type: "SET_META_IS_LOADING"; payload: boolean };

// Create the reducer function
const chatBarReducer = (state: ChatBarState, action: ChatBarAction): ChatBarState => {
  switch (action.type) {
    case "TOGGLE_RECORDING":
      return { ...state, isRecording: !state.isRecording };
    case "SET_AUDIO":
      return { ...state, isAudioOn: action.payload };
    case "SET_SUBMIT_ENABLED":
      return { ...state, isSubmitEnabled: action.payload };
    case "SET_CONVERSATION_TYPE":
      return {
        ...state,
        llmMessage: { ...state.llmMessage, state: { ...state.llmMessage.state, general: action.payload } },
      };
    case "SET_SCHOOL_MESSAGE":
      return {
        ...state,
        llmMessage: { ...state.llmMessage, state: { ...state.llmMessage.state, school: action.payload } },
      };
    case "SET_LEARNING_MESSAGE":
      return {
        ...state,
        llmMessage: { ...state.llmMessage, state: { ...state.llmMessage.state, learning: action.payload } },
      };
    case "SET_CONTEXT":
      return {
        ...state,
        llmMessage: {
          ...state.llmMessage,
          state: { ...state.llmMessage.state, context: action.payload.context, params: action.payload.params },
        },
      };
    case "SET_ACTIVE_CONTEXT_AND_OBJECT_ID":
      return { ...state, activeContext: action.payload.context, activeObjectId: action.payload.objectId };
    case "SET_ACTIVE_CHAT":
      return { ...state, activeChat: action.payload };
    case "SET_META_IS_LOADING":
      return {
        ...state,
        llmMessage: { ...state.llmMessage, meta: { ...state.llmMessage.meta, isLoading: action.payload } },
      };
    default:
      return state;
  }
};

type ChatBarContextType = {
  state: ChatBarState;
  toggleRecording: () => void;
  setAudio: (isAudioOn: boolean) => void;
  setSubmitEnabled: (isSubmitEnabled: boolean) => void;
  setConversationType: (conversationType: ConversationType) => void;
  setSchoolMessage: (schoolState: SchoolState) => void;
  setLearningMessage: (learningState: LearningState) => void;
  setContext: (context: { context: Context; params: { [key: string]: string } }) => void;
  setShowChat: (showChat: boolean) => void;
  setActiveContextAndObjectId: ({ context, objectId }: { context: Context; objectId: string }) => void;
  setActiveChat: ({ id, context }: { id: string; context: Context }) => void;
  showChat: boolean;
  mapRouteToContext: (route: keyof RootStackParamList, params?: unknown) => Context;
  setMetaIsLoading: (isLoading: boolean) => void;
  menuVisible: boolean;
  setMenuVisible: (menuVisible: boolean) => void;
  slideAnim: Animated.Value;
  handleMenuToggle: () => void;
  handleChatWindowOpenState: (open: boolean) => void;
};

// Create the context
const ChatBarContext = createContext<ChatBarContextType>({
  state: initialState,
  toggleRecording: () => {},
  setAudio: () => {},
  setSubmitEnabled: () => {},
  setConversationType: () => {},
  setSchoolMessage: () => {},
  setLearningMessage: () => {},
  setContext: () => {},
  setShowChat: () => {},
  setActiveContextAndObjectId: () => {},
  setActiveChat: () => {},
  showChat: false,
  mapRouteToContext: () => Context.GENERAL,
  setMetaIsLoading: () => {},
  menuVisible: false,
  setMenuVisible: () => {},
  slideAnim: new Animated.Value(0),
  handleMenuToggle: () => {},
  handleChatWindowOpenState: () => {},
});

// Create the provider component
export const ChatBarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { dispatch: appStateDispatch } = useAppState();
  const [state, dispatch] = useReducer(chatBarReducer, initialState);
  const [showChat, setShowChat] = useState(false);

  // Setup for the ChatWindow Slider Menu
  const [menuVisible, setMenuVisible] = useState(false);
  const slideAnim = useRef(new Animated.Value(0)).current; // Start at the default position

  const toggleRecording = useCallback(() => dispatch({ type: "TOGGLE_RECORDING" }), [dispatch]);
  const setAudio = useCallback((isAudioOn: boolean) => dispatch({ type: "SET_AUDIO", payload: isAudioOn }), [dispatch]);
  const setSubmitEnabled = useCallback(
    (isSubmitEnabled: boolean) => dispatch({ type: "SET_SUBMIT_ENABLED", payload: isSubmitEnabled }),
    [dispatch]
  );
  const setConversationType = useCallback(
    (conversationType: ConversationType) => dispatch({ type: "SET_CONVERSATION_TYPE", payload: conversationType }),
    [dispatch]
  );
  const setSchoolMessage = useCallback(
    (schoolState: SchoolState) => dispatch({ type: "SET_SCHOOL_MESSAGE", payload: schoolState }),
    [dispatch]
  );
  const setLearningMessage = useCallback(
    (learningState: LearningState) => dispatch({ type: "SET_LEARNING_MESSAGE", payload: learningState }),
    [dispatch]
  );
  const setContext = useCallback(
    (context: { context: Context; params: { [key: string]: string } }) =>
      dispatch({ type: "SET_CONTEXT", payload: context }),
    [dispatch]
  );
  const setActiveContextAndObjectId = useCallback(
    ({ context, objectId }: { context: Context; objectId: string }) =>
      dispatch({ type: "SET_ACTIVE_CONTEXT_AND_OBJECT_ID", payload: { context, objectId } }),
    [dispatch]
  );
  const setActiveChat = useCallback(
    ({ id, context }: { id: string; context: Context }) =>
      dispatch({ type: "SET_ACTIVE_CHAT", payload: { id, context } }),
    [dispatch]
  );
  const setMetaIsLoading = useCallback(
    (isLoading: boolean) => dispatch({ type: "SET_META_IS_LOADING", payload: isLoading }),
    [dispatch]
  );

  const handleChatWindowOpenState = useCallback(
    (open: boolean) => {
      appStateDispatch({
        type: appStateActions.SET_CONTENT,
        payload: { message: "" },
      });

      setShowChat(open);
    },
    [appStateDispatch]
  );

  const handleMenuToggle = useCallback(() => {
    if (menuVisible) {
      // Slide out to the left (hide)
      Animated.timing(slideAnim, {
        toValue: 0, // Back to the initial position
        duration: 300,
        useNativeDriver: true,
      }).start(() => setMenuVisible(false)); // Hide after animation
    } else {
      setMenuVisible(true); // Set visible before starting the animation
      // Slide in from the left
      Animated.timing(slideAnim, {
        toValue: 270, // Slide the container to the right
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [menuVisible, slideAnim]);

  return (
    <ChatBarContext.Provider
      value={{
        state,
        setContext,
        toggleRecording,
        setAudio,
        setSubmitEnabled,
        setConversationType,
        setSchoolMessage,
        setLearningMessage,
        setShowChat,
        showChat,
        setActiveContextAndObjectId,
        setActiveChat,
        mapRouteToContext,
        setMetaIsLoading,
        menuVisible,
        setMenuVisible,
        slideAnim,
        handleMenuToggle,
        handleChatWindowOpenState,
      }}>
      {children}
    </ChatBarContext.Provider>
  );
};

// Create a custom hook to use the context
export const useChatBar = () => {
  const context = useContext(ChatBarContext);
  if (context === undefined) {
    throw new Error("useChatBar must be used within a ChatBarProvider");
  }
  return context;
};
